import React, { useCallback, useContext, useEffect } from "react";
import { useNavigateWithSameKey } from "../../../../../../../../../../../custom-hooks/navigateWithKey";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  LoadingModalContext,
  MessagePopupContext,
} from "../../../../../../../../../ServicePage";
import {
  setPageClassState,
  setStepDefinitionState,
} from "../../../../../../../../../../../store/slices/test-automation/web/flow-stages/playwrightCSharpSpecflowSlices";
import PageClasses from "../../../../../shared/PageClasses";
import { Box, Button } from "@mui/material";
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
} from "@mui/icons-material";
import { handleProgressBar } from "../../../../../../../../../../custom-components/ProgressBar/ProgressBarWithAPI";
import { playWrightCSharpStepDefinition } from "../../../../../../../../../../../services/webServices";

const PageClassesPlaywrightCSharpSpecflow = () => {
  const navigate = useNavigateWithSameKey();
  const location = useLocation();
  const dispatch = useDispatch();

  const { locationKey } = location.state;

  const { featureFiles, pageClass, automationPath } = useSelector(
    (state) =>
      state["testAutomation"][locationKey]["webAutomation"][
        "playwrightCSharpSpecflow"
      ] || {}
  );

  const { setLoadingState } = useContext(LoadingModalContext);
  const { setMessage, setMessagePopup } = useContext(MessagePopupContext);

  useEffect(() => {
    if (!locationKey) {
      navigate("/landingpage");
      return;
    }
  }, [locationKey]);

  const handleBackClick = () => {
    // Navigate to the previous step (Test Case Imported)
    navigate(-1);
  };

  const handlePageClassUpdate = (updatedData) => {
    dispatch(setPageClassState(locationKey, updatedData));
  };

  const handleGenerateStepDefinition = async () => {
    if (!featureFiles || !pageClass) {
      setMessage("Missing required data for step definition generation");
      setMessagePopup(true);
      return;
    }

    let progressHandler = null;

    try {
      // Initialize progress bar
      progressHandler = handleProgressBar({
        setLoadingState,
        totalDurationMs: 18000,
      });
      progressHandler.start("Generating step definitions...");

      // Call API to generate step definitions
      const response = await playWrightCSharpStepDefinition({
        featureFileGenerated: featureFiles,
        pageClassData: pageClass,
      });

      // Store step definition in Redux
      dispatch(setStepDefinitionState(locationKey, response.data));

      // Navigate to step definition page
      navigate(`${automationPath}/step-definition`);
    } catch (error) {
      console.log(error);
      setMessage(
        error?.response?.data?.error ||
          "An error occurred while generating the step definitions."
      );
      setMessagePopup(true);
    } finally {
      setLoadingState({
        open: false,
        message: "",
        progress: null,
      });

      if (progressHandler) {
        progressHandler.stop();
      }
    }
  };

  return (
    <Box sx={{ height: "100%" }}>
      <PageClasses
        pageClass={pageClass}
        handlePageClass={handlePageClassUpdate}
        language={"csharp"}
      />
      {/* Navigation Buttons */}
      <Box
        sx={{
          p: 4,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: { xs: "wrap", sm: "nowrap" },
        }}
      >
        {/* Back Button */}
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={handleBackClick}
          sx={{
            borderColor: "#0094FB",
            color: "#0094FB",
            textTransform: "none",
            fontWeight: 500,
            fontFamily: "Manrope",
            borderRadius: "8px",
            borderWidth: "2px",
            px: 3,
            py: 1,
            minWidth: { xs: "100%", sm: "auto" },
            "&:hover": {
              borderColor: "#0078D4",
              backgroundColor: "rgba(0, 148, 251, 0.04)",
              borderWidth: "2px",
            },
          }}
        >
          Back
        </Button>

        {/* Continue Button */}
        <Button
          variant="contained"
          endIcon={<NextIcon />}
          onClick={handleGenerateStepDefinition}
          disabled={!pageClass || pageClass.length === 0}
          sx={{
            backgroundColor: "#0094FB",
            color: "white",
            textTransform: "none",
            fontWeight: 500,
            fontFamily: "Manrope",
            borderRadius: "8px",
            px: 3,
            py: 1,
            minWidth: { xs: "100%", sm: "auto" },
            "&:hover": {
              backgroundColor: "#0078D4",
            },
            "&:disabled": {
              backgroundColor: "#cccccc",
              color: "#666666",
            },
          }}
        >
          Generate Step Definition
        </Button>
      </Box>
    </Box>
  );
};

export default PageClassesPlaywrightCSharpSpecflow;
